// src/components/reports/SupervisorReport/SupervisorDetailedReport.tsx
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { SupervisorReportItem, formatCurrency, formatProfitRate } from '../../../../types/sales';
import { StyledTableCell } from './styles';
import { PrintButton, SimplePrintButton } from './Printing';
import { SupervisorChart } from './SupervisorChart';

interface SupervisorDetailedReportProps {
  data: SupervisorReportItem[];
  startMonth: string;
  endMonth: string;
  showPrintButtons?: boolean;
  chartTitle?: string; // 追加: チャートのタイトル
}

const SupervisorDetailedReport: React.FC<SupervisorDetailedReportProps> = ({
  data,
  startMonth,
  endMonth,
  showPrintButtons = false,
  chartTitle
}) => {
  // Get totals (last element is the total row)
  const getTotals = (): SupervisorReportItem | null => {
    if (!data || data.length === 0) return null;
    return data[data.length - 1];
  };

  // Get detail data (excluding total row)
  const getDetailData = (): SupervisorReportItem[] => {
    if (!data || data.length <= 1) return [];
    return data.slice(0, data.length - 1);
  };

  const detailData = getDetailData();
  const totals = getTotals();

  if (!totals || detailData.length === 0) return null;

  // Expense calculation logic - compare 1% of sales vs 5% of gross profit and use the lower value
  const calculateExpenses = (supervisor: SupervisorReportItem): number => {
    if (!supervisor.total_sales || !supervisor.final_profit) return 0;

    const salesExpense = Number(supervisor.total_sales) * 0.01; // 1% of sales
    const profitExpense = Number(supervisor.final_profit) * 0.05; // 5% of gross profit

    return Math.min(salesExpense, profitExpense);
  };

  // Calculate ratios
  const calculateRatios = (): {
    salesRatios: { [key: string]: number },
    profitRatios: { [key: string]: number }
  } => {
    const salesRatios: { [key: string]: number } = {};
    const profitRatios: { [key: string]: number } = {};

    const totalSales = Number(totals.total_sales) || 1; // Prevent division by zero
    const totalProfit = Number(totals.final_profit) || 1; // Prevent division by zero

    detailData.forEach(supervisor => {
      // const supervisorSales = Number(supervisor.total_sales) || 0;
      // const supervisorProfit = Number(supervisor.final_profit) || 0;
      const supervisorSales = Number(supervisor.total_sales) || 0;
      const supervisorProfit = Number(supervisor.final_profit) || 0;

      salesRatios[supervisor.supervisor_name] = Math.round((supervisorSales / totalSales) * 100);
      profitRatios[supervisor.supervisor_name] = Math.round((supervisorProfit / totalProfit) * 100);
    });

    return { salesRatios, profitRatios };
  };

  // Calculate ratios
  const { salesRatios, profitRatios } = calculateRatios();

  // Calculate total expenses
  const totalExpenses = detailData.reduce((acc, curr) => acc + calculateExpenses(curr), 0);

  return (
    <Box sx={{ width: '100%', mb: 6, position: 'relative' }}>
      {/* Print buttons */}
      {/* {showPrintButtons && (
        <>
          <PrintButton containerId="printable-report-section" />
          <SimplePrintButton />
        </>
      )} */}

      {/* Printable report section */}
      <Box id="printable-report-section">
        <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
          売上合計 {startMonth} 〜 {endMonth}
        </Typography>

        {/* Single table layout (solves alignment issues) */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell className="gray-cell" sx={{ width: '15%' }}>会社名</StyledTableCell>
                {detailData.map((supervisor, index) => (
                  <StyledTableCell
                    key={index}
                    align="center"
                    className="header-yellow"
                    sx={{ width: `${70 / detailData.length}%` }}
                  >
                    {supervisor.supervisor_name}
                  </StyledTableCell>
                ))}
                <StyledTableCell align="center" className="total-row" sx={{ width: '15%' }}>合計</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* 1. Sales, gross profit, profit rate rows */}
              <TableRow>
                <StyledTableCell className="green-cell">売上合計</StyledTableCell>
                {detailData.map((supervisor, index) => (
                  <StyledTableCell key={index} align="right">
                    {formatCurrency(supervisor.total_sales)}
                  </StyledTableCell>
                ))}
                <StyledTableCell align="right" className="total-row">
                  {formatCurrency(totals.total_sales)}
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell className="green-cell">合計粗利</StyledTableCell>
                {detailData.map((supervisor, index) => (
                  <StyledTableCell key={index} align="right">
                    {formatCurrency(supervisor.final_profit)}
                  </StyledTableCell>
                ))}
                <StyledTableCell align="right" className="total-row">
                  {formatCurrency(totals.final_profit)}
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell className="green-cell">粗利率 (%)</StyledTableCell>
                {detailData.map((supervisor, index) => (
                  <StyledTableCell key={index} align="right">
                    {formatProfitRate(supervisor.final_profit_rate)}
                  </StyledTableCell>
                ))}
                <StyledTableCell align="right" className="total-row">
                  {formatProfitRate(totals.final_profit_rate)}
                </StyledTableCell>
              </TableRow>

              {/* 2. Expenses row (with spacer row) */}
              <TableRow>
                <StyledTableCell colSpan={detailData.length + 2} sx={{ height: '40px', p: 0 }} />
              </TableRow>
              <TableRow>
                <StyledTableCell className="expenses">
                  経費
                </StyledTableCell>
                {detailData.map((supervisor, index) => {
                  const expenseValue = calculateExpenses(supervisor);
                  return (
                    <StyledTableCell key={index} align="right">
                      {formatCurrency(expenseValue)}
                    </StyledTableCell>
                  );
                })}
                <StyledTableCell align="right" className="total-row">
                  {formatCurrency(totalExpenses)}
                </StyledTableCell>
              </TableRow>

              {/* 3. Ratio rows (with spacer row) */}
              <TableRow>
                <StyledTableCell colSpan={detailData.length + 2} sx={{ height: '40px', p: 0 }} />
              </TableRow>
              <TableRow>
                <StyledTableCell className="pink-cell">全体売上比率 (%)</StyledTableCell>
                {detailData.map((supervisor, index) => (
                  <StyledTableCell key={index} align="right">
                    {salesRatios[supervisor.supervisor_name]}%
                  </StyledTableCell>
                ))}
                <StyledTableCell align="right" className="total-row">
                  100%
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell className="pink-cell">全体利益比率 (%)</StyledTableCell>
                {detailData.map((supervisor, index) => (
                  <StyledTableCell key={index} align="right">
                    {profitRatios[supervisor.supervisor_name]}%
                  </StyledTableCell>
                ))}
                <StyledTableCell align="right" className="total-row">
                  100%
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* 共通のSupervisorChartコンポーネントを使用 */}
        <Box sx={{ mt: 8 }}>
          <SupervisorChart
            data={data}
            title={chartTitle || "担当者別 売上・粗利チャート"}
            fullSize={false} // レポートビューでは小さいサイズで表示
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SupervisorDetailedReport;